import { TFunction } from '@spotify-internal/i18n-core';
import { DecisionReason, TrackDetails } from '@hooks/useTrackInfo/types';
import { EMPTY_VALUE } from '@helpers/constants';

export const getReasonTranslationKey = (
  reason: DecisionReason,
  t: TFunction,
) => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
      return t(
        'atlas.unassociated_videos.reason.block_patch',
        'Track not eligible for association',
        'Reason for an unassociated video that is not eligible for associations',
      );
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
      return t(
        'atlas.unassociated_videos.reason.no_release_candidates',
        'No related release candidates',
        'Reason for an unassociated video that does not has an release candidate',
      );
    case DecisionReason.TRACK_NAME_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.name_mismatch',
        'Track name mismatch',
        'Reason for an unassociated video that has a name mismatch between the track and the video',
      );
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.artist_mismatch',
        'Track artist mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their artists',
      );
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.explicitness_mismatch',
        'Track explicitness mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their explicitness',
      );
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason.duration_mismatch',
        'Track duration mismatch',
        'Reason for an unassociated video that has a name mismatch between the track, video and their duration',
      );
    default:
      return t(
        'atlas.unassociated_videos.reason.unknown',
        'Unknown',
        'Unknown reason for an unassociated video',
      );
  }
};

export const getReasonDescriptionTranslationKey = (
  reason: DecisionReason,
  t: TFunction,
) => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
      return t(
        'atlas.unassociated_videos.reason_description.block_patch',
        'One or both tracks are not currently eligible for association.',
        'Reason description for when an unassociated video is not eligible for associations',
      );
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
      return t(
        'atlas.unassociated_videos.reason_description.no_release_candidates',
        'The Related release ISRC has not been found or does not match.',
        'Reason description for when an unassociated video does not has an release candidate',
      );
    case DecisionReason.TRACK_NAME_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.name_mismatch',
        "Both tracks' names should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track and the video',
      );
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.artist_mismatch',
        "Both tracks' contributing artists and roles should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their artists',
      );
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.explicitness_mismatch',
        "Both tracks' explicitness should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their explicitness',
      );
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return t(
        'atlas.unassociated_videos.reason_description.duration_mismatch',
        "Both tracks' durations should be an exact match.",
        'Reason description for when an unassociated video has a name mismatch between the track, video and their duration',
      );
    default:
      return EMPTY_VALUE;
  }
};

export const getMetadataAttributeByReason = (
  reason: DecisionReason,
):
  | keyof Pick<TrackDetails, 'name' | 'artists' | 'explicitLyrics' | 'duration'>
  | undefined => {
  switch (reason) {
    case DecisionReason.PARTIAL_BLOCK_PATCH:
    case DecisionReason.TOTAL_BLOCK_PATCH:
    case DecisionReason.NO_RELATED_RELEASE_CANDIDATES:
    case DecisionReason.TRACK_LICENSOR_MISMATCH:
      return undefined;
    case DecisionReason.TRACK_NAME_MISMATCH:
      return 'name';
    case DecisionReason.TRACK_ARTIST_MISMATCH:
      return 'artists';
    case DecisionReason.TRACK_EXPLICITNESS_MISMATCH:
      return 'explicitLyrics';
    case DecisionReason.TRACK_DURATION_MISMATCH:
      return 'duration';
    default:
      return undefined;
  }
};
